import { Micro, PhaseTrack, SerifHeading } from '../primitives';
import type { Race } from '../../fixtures/race';
import type { ProactiveTriggerType } from '../../lib/proactiveCopy';
import { proactiveTypeReadable } from '../../lib/proactiveKicker';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { formatGoalTime } from '../../lib/goalTime';
import { PhaseLabelsV2, type NoRacePhase } from './NoRaceCard';
import styles from './ChampionshipCard.module.css';

const SHORT_WEEKDAYS: Record<string, string> = {
  Sun: 'Sunday',
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
};
const SHORT_MONTHS: Record<string, string> = {
  Jan: 'January',
  Feb: 'February',
  Mar: 'March',
  Apr: 'April',
  May: 'May',
  Jun: 'June',
  Jul: 'July',
  Aug: 'August',
  Sep: 'September',
  Sept: 'September',
  Oct: 'October',
  Nov: 'November',
  Dec: 'December',
};
const SHORT_RACE_DATE = /^(Sun|Mon|Tue|Wed|Thu|Fri|Sat)\.?,?\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sept|Sep|Oct|Nov|Dec)\.?\s+(\d{1,2})$/;

/**
 * 2.0 (string 69): the race card prints "Saturday, October 31". `Race.date`
 * arrives preformatted by `useUpcomingRace` ("Sat, Oct 31"), so the long form
 * is spelled out from that string, with no clock and no date arithmetic.
 * Anything that is not that shape is returned as it came.
 */
export function longRaceDate(date: string): string {
  const m = SHORT_RACE_DATE.exec(date.trim());
  if (!m) return date;
  return `${SHORT_WEEKDAYS[m[1]!]}, ${SHORT_MONTHS[m[2]!]} ${m[3]}`;
}

/** 2.0 (string 70): "4 days to go". The count of 1 reads "1 day to go"; the
 *  count of 0 reads "Race day" (ruling 12, 2026-10-17). */
export function daysToGoLabel(daysAway: number): string {
  if (daysAway === 0) return 'Race day';
  return `${daysAway} ${daysAway === 1 ? 'day' : 'days'} to go`;
}

/** A `Race` that may also carry the viewer's goal in whole seconds
 *  (`useUpcomingRace`'s `goalTimeSeconds`, W2-GOAL-LINE). Absent = no goal. */
export type RaceWithGoal = Race & { goalTimeSeconds?: number | null };

/** 2.0 (string 71, W2-GOAL-LINE GL-3): "Goal 19:30", printed under the count only
 *  when the viewer's goal is a positive number of seconds; otherwise null and the
 *  card draws no line and leaves no gap. Used by the phone and the desktop card. */
export function goalLineLabel(goalTimeSeconds: number | null | undefined): string | null {
  if (typeof goalTimeSeconds !== 'number' || !(goalTimeSeconds > 0)) return null;
  const text = formatGoalTime(goalTimeSeconds);
  return text === '' ? null : `Goal ${text}`;
}

/** 2.0: `Race.phase` ('Peak' is the competition phase, printed "Comp") to the
 *  card's phase keys; anything unknown underlines nothing. */
export function racePhaseKey(phase: Race['phase'] | string | undefined): NoRacePhase | null {
  switch (phase) {
    case 'Base':
      return 'base';
    case 'Build':
      return 'build';
    case 'Peak':
      return 'comp';
    case 'Taper':
      return 'taper';
    default:
      return null;
  }
}

type ChampionshipCardProps = {
  race: RaceWithGoal;
  /** When true, show the motivational coach embedded line below the phase
   *  track. Mobile variant B = true; variant A suppresses it. */
  withMotivation?: boolean;
  /**
   * Phase 6 cross-cutting PR 6 — when a proactive trigger fired this
   * load, replace the "Motivational Coach" sub-label with
   * `RELATED · <type-readable>` and add a `--today` left-rule to the
   * moti block. Surfaces the PR 4f cross-module handoff to the user.
   *
   * Null/undefined → default "Motivational Coach" label, no left-rule.
   */
  proactiveType?: ProactiveTriggerType | null;
  /**
   * B62 — the ▸ in the card's top-right. It shipped as a bare `<button>` with
   * no `onClick`: an enabled, focusable, hit-testable control that did
   * literally nothing when tapped (founder, Build 26).
   *
   * WHY THE SEASON PLAN, and not a race detail. `aria-label` said "Race
   * details", but there is no race-detail route in this app — `App.tsx`
   * registers `/races` (the log/add list) and no `/races/:id`. The two honest
   * options were therefore: delete the affordance, or point it somewhere real.
   * It points at the season plan because that is where the facts this card
   * renders actually live — the countdown, the phase track and the goal are
   * season-plan facts — and because `<ChampionshipCardDesktop>`, the same card
   * in the desktop rail, has always ended in a `Season plan ›` CTA that
   * navigates to `/plan`. One card, two variants, one destination.
   *
   * Omitted → the button is ABSENT from the DOM. A card with no handler must
   * not render a control, which is the whole defect this prop closes.
   */
  onOpenSeasonPlan?: () => void;
  /**
   * 2.0 Today (W1-TODAY-CARDS, TC-5; Part 1 frame 1q). Opt-in: true draws the
   * ratified race card on the Part 4 ramp: kicker "County Championship · 5 km ·
   * Saturday, October 31", "4 days to go" in DM Sans, "Goal 19:30" under it only
   * when the race carries the viewer's `goalTimeSeconds` (W2-GOAL-LINE, closes
   * wave 1 D4; otherwise no line and no gap), phase labels with the current one
   * underlined, the generated line under today's author label (D5). Absent/false
   * = the legacy card, byte-identical.
   */
  v2?: boolean;
};

/**
 * Championship Final card — dark ink-900 background with race countdown,
 * phase track, and optional motivational coach embedded line.
 *
 * Mobile-optimized layout; desktop uses a narrower variant in the right
 * rail (wired in step 7). No-race variant ships in step 9.
 */
export function ChampionshipCard({
  race,
  withMotivation = true,
  proactiveType,
  onOpenSeasonPlan,
  v2 = false,
}: ChampionshipCardProps) {
  const iconsOn = useIconSystem();
  const relatedLabel = proactiveTypeReadable(proactiveType);
  const hasHint = relatedLabel !== null;
  const goalLine = goalLineLabel(race.goalTimeSeconds);

  if (v2) {
    return (
      <section className={`${styles.champ} ${styles.v2Card} onDark`}>
        <div className={styles.v2Top}>
          <div className={styles.v2Head}>
            <div className={styles.v2Kicker}>
              {race.name} · {race.distance} · {longRaceDate(race.date)}
            </div>
            <div className={styles.v2Count}>{daysToGoLabel(race.daysAway)}</div>
            {goalLine && <div className={styles.v2Goal}>{goalLine}</div>}
          </div>
          {onOpenSeasonPlan && (
            <button
              type="button"
              className={styles.v2Go}
              onClick={onOpenSeasonPlan}
              aria-label="Open season plan"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M4 2l6 5-6 5" />
              </svg>
            </button>
          )}
        </div>

        <PhaseLabelsV2 current={racePhaseKey(race.phase)} />

        {withMotivation && (
          <div className={`${styles.v2Moti} ${hasHint ? styles.v2MotiHinted : ''}`.trim()}>
            <div className={styles.v2MotiFrom}>
              <span className={styles.v2MotiDot} aria-hidden />
              {hasHint ? `RELATED · ${relatedLabel}` : 'Motivational Coach'}
            </div>
            <div className={styles.v2MotiText}>{race.motivationalLine}</div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={styles.champ}>
      <div className={styles.top}>
        <div className={styles.left}>
          <Micro as="div" style={{ color: 'rgba(255, 255, 255, 0.55)' }}>
            {race.name} · {race.distance} · {race.date}
          </Micro>
          <SerifHeading level="sm" as="div" className={styles.title}>
            <em className={styles.titleEm}>{race.daysAway}</em> days
            <span className={styles.titleSep}>·</span>
            Goal {race.goalTime}
          </SerifHeading>
        </div>
        {onOpenSeasonPlan && (
          <button
            type="button"
            className={styles.go}
            onClick={onOpenSeasonPlan}
            aria-label="Open season plan"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M4 2l6 5-6 5" />
            </svg>
          </button>
        )}
      </div>

      <div className={styles.track}>
        <PhaseTrack phase={race.phase} progressPct={race.progressPct} variant="dark" />
      </div>

      {withMotivation && (
        <div className={`${styles.moti} ${hasHint ? styles.motiHinted : ''}`}>
          {/* A2 · R8 — `kudo.star` (Sparkle) @14, and EXPLICITLY NOT
              `chip.reward`. The audit offered this slot as a candidate mount
              for the reward token; the founder ruled against it so the fill
              vocabulary stays honest — this glyph means VOICE, not EARNED, and
              `chip.reward`'s solid star is reserved for the latter (it mounts
              on the DaysDoneRing terminal state instead, per R2).
              `kudo.star` is hollow, which is exactly the register wanted. */}
          <div className={styles.motiGlyph} aria-hidden>
            {iconsOn ? (
              <Icon name="kudo.star" size={14} />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              >
                <path d="M10 2l2.3 5 5.2.7-3.8 3.6.9 5.2L10 13.9 5.4 16.5l.9-5.2L2.5 7.7l5.2-.7z" />
              </svg>
            )}
          </div>
          <div className={styles.motiBody}>
            <div className={styles.motiFrom}>
              {hasHint ? `RELATED · ${relatedLabel}` : 'Motivational Coach'}
            </div>
            <div className={styles.motiText}>{race.motivationalLine}</div>
          </div>
        </div>
      )}
    </section>
  );
}
